import {CardMediaComponent} from '@/components/ui/card-media';
import ConfirmationDialog from '@/components/ui/dialogs/confirmation-dialog';
import {useSnackbar} from '@/contexts/snackbar';
import {removeCompany} from '@/rest/admin/companies/remove-company';
import {Company} from '@/types/company';
import {useTitleStyle} from '@/utils';

import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {useCallback, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';

interface CompanyCardProps {
  company: Company;
}

const CompanyCard: React.FC<CompanyCardProps> = ({company}) => {
  const titleStyle = useTitleStyle('18ch', undefined, 'xl', true);

  const {t} = useTranslation();

  const {showSnackbarMessage} = useSnackbar();

  const theme = useTheme();
  const isXsScreen = useMediaQuery(theme.breakpoints.down('xs'));

  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [showMainTooltip, setShowMainTooltip] = useState(false);

  const logoSrc = `${company.logo}?timestamp=${company.updatedAt}`;

  const handleDeleteClick = useCallback(() => {
    if (company._id) {
      const companyId = company._id;

      removeCompany(companyId)
        .then((response) => {
          if (response) {
            showSnackbarMessage(t('company_deleting_success'), 'success');
          } else {
            showSnackbarMessage(t('company_deleting_failure'), 'error');
          }
        })
        .catch((error) => {
          console.error('Error deleting event:', error);
          showSnackbarMessage(t('group_deleting_failure'), 'error');
        })
        .finally(() => {
          setDeleteConfirmationOpen(false);
        });
    }
  }, [company, showSnackbarMessage, t]);

  const handleDeleteConfirmation = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setDeleteConfirmationOpen(true);
    },
    []
  );

  const handleCloseConfirmationDialog = useCallback(() => {
    setDeleteConfirmationOpen(false);
  }, []);

  const handleButtonMouseEnter = useCallback(() => {
    setShowMainTooltip(false);
  }, []);

  const handleButtonMouseLeave = useCallback(() => {
    setShowMainTooltip(true);
  }, []);

  const handleCardMouseEnter = useCallback(() => {
    setShowMainTooltip(true);
  }, []);

  const handleCardMouseLeave = useCallback(() => {
    setShowMainTooltip(false);
  }, []);

  return (
    <Grid item xs={12} sm={12} md={12} lg={12}>
      <Tooltip title={t('open_company')} open={showMainTooltip}>
        <Link
          to={`/admin/company/${company._id}`}
          style={{textDecoration: 'none'}}
        >
          <Card
            onMouseEnter={handleCardMouseEnter}
            onMouseLeave={handleCardMouseLeave}
            sx={{
              width: '100%',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: {xs: 'column', md: 'row'}
            }}
          >
            <Grid container alignItems={isXsScreen ? 'flex-start' : 'center'}>
              <Grid
                item
                xs={12}
                sm={12}
                md={4}
                lg={4}
                container
                sx={{
                  justifyContent: {
                    xs: 'center',
                    md: 'flex-start'
                  }
                }}
              >
                {company.logo && (
                  <CardMediaComponent
                    height={80}
                    image={logoSrc}
                    altText={t('company_logo')}
                    width={240}
                    objectFit={'contain'}
                    sx={{
                      boxShadow: 0
                    }}
                  />
                )}
              </Grid>

              <Grid item xs={12} sm={12} md={4} lg={4}>
                <CardContent
                  sx={{
                    paddingLeft: '15px'
                  }}
                >
                  <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="flex-start"
                    alignItems={'flex-start'}
                    flexGrow={1}
                    sx={{
                      alignItems: {xs: 'center', md: 'flex-start'}
                    }}
                  >
                    <Typography
                      gutterBottom
                      variant="h5"
                      component="div"
                      sx={titleStyle}
                    >
                      {company.title}
                    </Typography>
                  </Box>
                </CardContent>
              </Grid>

              <Grid item xs={12} sm={12} md={4} lg={4}>
                <Box
                  display="flex"
                  justifyContent={'center'}
                  alignItems="center"
                  sx={{
                    marginRight: {xs: 0, md: '40px'},
                    marginBottom: {xs: '20px', md: 0}
                  }}
                >
                  <Tooltip title={t('delete')}>
                    <Button
                      onMouseEnter={handleButtonMouseEnter}
                      onMouseLeave={handleButtonMouseLeave}
                      variant="contained"
                      color="primary"
                      onClick={handleDeleteConfirmation}
                      sx={{
                        minWidth: '100px',
                        width: {xs: '125px', sm: '150px'}
                      }}
                    >
                      {t('delete')}
                    </Button>
                  </Tooltip>
                </Box>
              </Grid>
            </Grid>
          </Card>
        </Link>
      </Tooltip>
      <ConfirmationDialog
        open={deleteConfirmationOpen}
        title={t('delete_company_dialog_title')}
        message={t('delete_company_dialog_content')}
        onConfirm={handleDeleteClick}
        onCancel={handleCloseConfirmationDialog}
        warn
      />
    </Grid>
  );
};

export default CompanyCard;
